import React, { useEffect, useState } from 'react'
import { Check, FileCheck, FileKey, Fingerprint, ShieldCheck, Smartphone } from 'lucide-react'
import {
    loadLocalWalletStatus,
    NotLocalWalletAccountError,
    walletSafetyRows,
    walletSetupRemaining,
    type LocalWalletStatus,
    type WalletSafetyAction,
} from 'lib/localWallet'
import { downloadWalletBackup } from 'lib/walletBackup'
import { passkeyBrowserUnavailableReason } from 'lib/walletPasskeyWebAuthn'
import { formatSats } from './WalletSetup'
import { OPEN_WALLET_FILE_CHECK_EVENT, OPEN_WALLET_SETUP_EVENT } from './WalletSetupHost'
import styles from '../modals/wallet.module.scss'

export type LocalWalletAccount = {
    /** Setup status for a browser-wallet account; null for every other account. */
    status: LocalWalletStatus | null
    /** true: a browser-wallet account; false: not one; null: still loading, or the check failed. */
    isLocal: boolean | null
    setStatus: (status: LocalWalletStatus) => void
}

export function useLocalWalletStatus(enabled: boolean): LocalWalletAccount {
    const [status, setStatus] = useState<LocalWalletStatus | null>(null)
    const [isLocal, setIsLocal] = useState<boolean | null>(null)
    useEffect(() => {
        if (!enabled) return undefined
        let cancelled = false
        loadLocalWalletStatus()
            .then(next => {
                if (cancelled) return
                setStatus(next)
                setIsLocal(true)
            })
            .catch(error => {
                if (!cancelled && error instanceof NotLocalWalletAccountError) setIsLocal(false)
            })
        return () => {
            cancelled = true
        }
    }, [enabled])
    return { status, isLocal, setStatus }
}

const SAFETY_ROW_COPY = {
    file: {
        label: 'Wallet file',
        done: 'Saved and checked',
        setup: 'Not saved yet',
        fileCheck: 'Saved. Check that it opens your wallet.',
        cta: { setup: 'Save it', fileCheck: 'Check it' },
    },
    passkey: {
        label: 'Passkey',
        done: 'Added',
        setup: 'Restore your wallet with your screen lock: face, fingerprint or passcode.',
        unavailable: "This browser can't make passkeys.",
        cta: { setup: 'Add it' },
    },
    device: {
        label: 'This device',
        done: 'Holds your keys',
        unlock: "Doesn't hold your keys",
        checking: 'Checking…',
        cta: { unlock: 'Unlock' },
    },
} as const

const SAFETY_ICONS = { file: FileKey, passkey: Fingerprint, device: Smartphone }

// Browser-wallet accounts: the wallet file, the passkey and this device in one card,
// each done or with the action that finishes it (and the sats it still earns).
export default function WalletSafetyCard({
    status,
    userId,
    deviceHasKeys,
    onOpen,
    onSaved,
}: {
    status: LocalWalletStatus | null
    userId: string
    deviceHasKeys: boolean | undefined
    onOpen: () => void
    onSaved: () => Promise<void>
}) {
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    if (!status?.registered) return null
    const passkeysAvailable = !passkeyBrowserUnavailableReason()
    const remaining = walletSetupRemaining(status, passkeysAvailable)
    const signupGiftWaiting =
        status.gift_sats > 0 &&
        (['download', 'passkey'] as const).some(step => {
            const earned = step === 'download' ? status.backup_file_saved : status.passkey_verified
            const gift = remaining.giftFor(step)
            return earned && (!gift || gift.status === 'pending')
        })
    const rows = walletSafetyRows(status, deviceHasKeys, passkeysAvailable)
    const act = (action: WalletSafetyAction) => {
        if (!action) return
        onOpen()
        // The setup covers saving the file and adding the passkey; on a device without
        // the keys it opens the restore (passkey or wallet file) instead.
        window.dispatchEvent(
            new Event(
                action === 'fileCheck' ? OPEN_WALLET_FILE_CHECK_EVENT : OPEN_WALLET_SETUP_EVENT,
            ),
        )
    }
    const saveCopy = async () => {
        if (saving) return
        setSaving(true)
        setError('')
        try {
            await downloadWalletBackup('client_shares', userId)
            await onSaved()
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'The wallet file was not saved.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <section
            className={styles.selfCustodySetupCard}
            aria-label="Wallet safety"
            data-testid="wallet-safety-card"
        >
            <div className={styles.selfCustodySetupHeader}>
                <ShieldCheck size={20} aria-hidden="true" />
                <div>
                    <div className={styles.selfCustodySetupTitle}>Wallet safety</div>
                    <div className={styles.selfCustodySetupSubtitle}>
                        Only you hold this wallet, so Treechat can&apos;t recover it for you.
                    </div>
                </div>
            </div>
            {signupGiftWaiting && (
                <button
                    type="button"
                    className={styles.walletSetupPill}
                    onClick={() => act('setup')}
                >
                    <span>Your welcome gift is waiting</span>
                    <span className={styles.walletSetupPillReward}>Get my sats</span>
                </button>
            )}
            <ul className={styles.walletSafetyRows}>
                {rows.map(row => {
                    const copy = SAFETY_ROW_COPY[row.key] as Record<string, any>
                    const Icon = SAFETY_ICONS[row.key]
                    const detail =
                        row.state === 'done'
                            ? copy.done
                            : row.state === 'todo'
                            ? copy[row.action as string]
                            : copy[row.state]
                    return (
                        <li key={row.key} className={styles.walletSafetyRow}>
                            <Icon
                                size={18}
                                aria-hidden="true"
                                className={styles.walletSafetyIcon}
                            />
                            <div className={styles.walletSafetyText}>
                                <div className={styles.walletSafetyLabel}>{copy.label}</div>
                                <div className={styles.walletSafetyDetail}>{detail}</div>
                            </div>
                            {row.state === 'done' ? (
                                <Check
                                    size={20}
                                    aria-label="Done"
                                    className={styles.walletSafetyDone}
                                />
                            ) : row.action ? (
                                <button
                                    type="button"
                                    className={styles.walletSafetyAction}
                                    onClick={() => act(row.action)}
                                    data-testid={`wallet-safety-${row.key}`}
                                >
                                    {copy.cta[row.action]}
                                    {row.rewardSats > 0 && (
                                        <span className={styles.walletSetupPillReward}>
                                            +{formatSats(row.rewardSats)}
                                        </span>
                                    )}
                                </button>
                            ) : null}
                        </li>
                    )
                })}
            </ul>
            {deviceHasKeys && status.backup_file_saved && (
                <button
                    type="button"
                    className={styles.walletSafetyLink}
                    onClick={() => void saveCopy()}
                    disabled={saving}
                >
                    {saving ? 'Saving…' : 'Save another copy of the wallet file'}
                </button>
            )}
            {error && (
                <p className={styles.selfCustodySetupError} role="alert">
                    {error}
                </p>
            )}
        </section>
    )
}

// The Wallet modal pill for the optional file check reward, under the balance.
export function WalletFileCheckPill({
    status,
    onOpen,
}: {
    status: LocalWalletStatus | null
    onOpen: () => void
}) {
    if (!status?.registered) return null
    const remaining = walletSetupRemaining(status, !passkeyBrowserUnavailableReason())
    if (!remaining.fileCheckOffered) return null
    return (
        <button
            type="button"
            className={styles.walletSetupPill}
            onClick={() => {
                onOpen()
                window.dispatchEvent(new Event(OPEN_WALLET_FILE_CHECK_EVENT))
            }}
            data-testid="wallet-file-check-pill"
        >
            <FileCheck size={16} aria-hidden="true" />
            <span>Check wallet file</span>
            <span className={styles.walletSetupPillReward}>
                +{formatSats(status.gift_step_sats.file_check)} sats
            </span>
        </button>
    )
}

// The button on the "check your wallet file" notification. Only the owner's own
// system post gets it; the overlay handles a file that was already checked.
export function WalletFileCheckAction({
    answer,
    isOwner,
}: {
    answer: { is_system?: boolean; delta_json?: { wallet_file_check?: { sats?: number } } }
    isOwner: boolean
}) {
    const meta = answer?.delta_json?.wallet_file_check
    if (!meta || !answer?.is_system || !isOwner) return null
    const sats = Number(meta.sats) || 0
    return (
        <button
            type="button"
            className={styles.walletSetupPill}
            onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                window.dispatchEvent(new Event(OPEN_WALLET_FILE_CHECK_EVENT))
            }}
            data-testid="wallet-file-check-noti"
        >
            <FileCheck size={16} aria-hidden="true" />
            <span>Check wallet file</span>
            {sats > 0 && (
                <span className={styles.walletSetupPillReward}>+{formatSats(sats)} sats</span>
            )}
        </button>
    )
}
